import { useEffect, useState } from "react";
import { getToken } from "../../../services/auth";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import Loader from "../../../components/modal/Loader";
import Button from "../../../components/button/Button";
import InputText from "../../../components/inputText/InputText";
import Pagination from "../../../components/pagination/Pagination";
import Modal from "../../../components/modal/Modal";
import { config } from '../../../config/env';

const fetchTopics = async ({
  page = 1,
  limit = 10,
  sort_by = "created_at",
  order = "desc",
  search = "",
}) => {
  try {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("limit", limit);
    params.append("sort_by", sort_by);
    params.append("order", order);
    if (search) {
      params.append("search", search);
    }

    const response = await fetch(
      `${config.apiBaseUrl}/api/v1/admin/topics/?${params.toString()}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await getToken()}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch topics");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching topics:", error);
    throw error;
  }
};

const updateTopicArchiveStatus = async (topicId, isArchived) => {
  try {
    const response = await fetch(
      `${config.apiBaseUrl}/api/v1/admin/topics/${topicId}/archive`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await getToken()}`,
        },
        body: JSON.stringify({ is_archived: isArchived }),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to update topic archive status");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

const Topics = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const defaultState = {
    filter: {
      search: "",
      page: 1,
      limit: 10,
      sort_by: "created_at",
      order: "desc",
    },
    data: [],
    total: 0,
    total_by_filter: 0,
    is_loading: false,
  };

  const defaultModalState = {
    title: "",
    subtext: "",
    hasCancelButton: false,
    confirmButtonLabel: "",
    icon: null,
    loading: false,
    onConfirmHandler: null,
    onCancelHandler: null,
    show: false,
  };

  const [state, setState] = useState(defaultState);
  const [resetCount, setResetCount] = useState(0);
  const [modalState, setModalState] = useState(defaultModalState);

  const updateQuery = (pairs) => {
    if (pairs.length === 0) {
      navigate("", { replace: true });
      return;
    };

    const newSearchParams = new URLSearchParams(searchParams.toString());
    pairs.forEach((element) => {
      newSearchParams.set(element[0], element[1]);
    });
    navigate(`?${newSearchParams.toString()}`, { replace: true });
  };

  const loadTopics = async () => {
    setState((prevState) => ({ ...prevState, is_loading: true }));
    const topicsData = await fetchTopics({
      page: state.filter.page,
      limit: state.filter.limit,
      sort_by: state.filter.sort_by,
      order_by: state.filter.order_by,
      search: state.filter.search,
    });
    setState((prevState) => ({
      ...prevState,
      data: topicsData.data,
      total: topicsData.total,
      total_by_filter: topicsData.count,
      is_loading: false,
    }));
  }

  const addTopicHandler = () => {
    navigate("/ava-admin/topics/add");
  };

  const filterHandler = async () => {
    console.log("Filter applied:", state.filter);
    setState((prevState) => ({ 
      ...prevState, 
      filter: { 
        ...prevState.filter, page: 1 
      } 
    }));
    let pairs = [
      ["page", "1"],
    ];
    if (state.filter.search) {
      pairs.push(["search", state.filter.search]);
    } else {
      pairs.push(["search", ""]);
    }
    updateQuery(pairs);
    setResetCount((prevCount) => prevCount + 1);
  }

  const resetFilterHandler = () => {
    console.log("Resetting filters");
    setState((prevState) => ({
      ...prevState,
      filter: {
        ...prevState.filter,
        search: "",
        page: 1,
      },
    }));
    updateQuery([]);
    setResetCount((prevCount) => prevCount + 1);
  }

  const pageChangeHandler = (newPage) => {
    console.log("Page changed:", newPage);
    setState((prevState) => ({
      ...prevState,
      filter: {
        ...prevState.filter,
        page: newPage,
      },
    }));
    updateQuery([["page", newPage]]);
    setResetCount((prevCount) => prevCount + 1);
  }

  const archiveClickHandler = async (item) => {
    console.log("Archive clicked for item:", item);
    setModalState({
      show: true,
      title: item.is_archived ? "Unarchive Topic" : "Archive Topic",
      subtext: item.is_archived ? `Are you sure you want to unarchive the topic "${item.name}"?` : `Are you sure you want to archive the topic "${item.name}"?`,
      hasCancelButton: true,
      confirmButtonLabel: item.is_archived ? "Unarchive" : "Archive",
      icon: "question",
      loading: false,
      onConfirmHandler: async () => {
        try {
          setModalState((prevState) => ({ ...prevState, loading: true }));
          setState((prevState) => ({ ...prevState, is_loading: true }));
          await updateTopicArchiveStatus(item.id, !item.is_archived);
          setModalState(defaultModalState);
          // Reload topics after archiving/unarchiving
          await loadTopics();
        } catch (error) {
          console.error("Error updating archive status:", error);
          setState(prevState => ({ ...prevState, is_loading: false }));
          setModalState(defaultModalState);
          setModalState((prevState) => ({ ...prevState, 
            title: "Error",
            subtext: "An error occurred while updating the topic's archive status. Please try again.",
            hasCancelButton: false,
            confirmButtonLabel: "Close",
            icon: "error",
            loading: false,
            onConfirmHandler: () => setModalState(defaultModalState),
          }));
        }
      },
      onCancelHandler: async () => {
        setModalState(defaultModalState);
      }
    });
  }

  useEffect(() => {
      console.log(searchParams.toString());
      // Check if there are any search params and set state accordingly
      if (searchParams.has('search') || searchParams.has('page')) {
        setState((prevState) => ({
          ...prevState,
          filter: {
            ...prevState.filter,
            search: searchParams.get('search') || "",
            page: parseInt(searchParams.get('page')) || 1,
          },
        }));
      }
      setResetCount((prevCount) => prevCount + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (resetCount === 0) return;

      const initialized = async () => {
        await loadTopics();
      }
      initialized();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetCount]);

    const classNamesIsArchived = (isArchived) => {
      return isArchived ? "text-gray-400" : "text-black";
    }

    const renderList = () => {
      if (!state.data || state.data.length === 0) {
        return (
          <div className="flex justify-center items-center h-[200px]">
            <div className="text-gray-500 text-lg font-semibold">
              No topics found.
            </div>
          </div>
        );
      }

      const renderAction = (item) => {
        if (item.is_archived) {
          return (
            <button
              onClick={() => archiveClickHandler(item)}
              className="text-gray-600 hover:text-gray-900 px-5"
            >
              {item.is_archived ? "Unarchive" : "Archive"}
            </button>
          )
        }

        const renderEditButton = (item) => {
          if (item.is_archived) {
            return null;
          }
          return (
            <a href={`/ava-admin/topics/update/${item.id}`} className="text-indigo-600 hover:text-indigo-900 px-5">
              Edit<span className="sr-only">, {item.id} </span>
            </a>
          )
        };

        return (
          <>
            { renderEditButton(item) }
            <button
              onClick={() => archiveClickHandler(item)}
              className="text-red-600 hover:text-red-900 px-5"
            >
              {item.is_archived ? "Unarchive" : "Archive"}
            </button>
          </>
        );
      }

      return (
        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <table className="relative min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
                      Name
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Description
                    </th>
                    <th scope="col" className="py-3.5 pl-3 pr-4 sm:pr-0">
                      <span className="sr-only">Edit</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {state.data.map((item) => (
                    <tr key={item.id} className={`hover:bg-gray-50 ${classNamesIsArchived(item.is_archived)}`}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium sm:pl-0">
                        {item.name}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm">{item.description}
                      </td>
                      <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                        {renderAction(item)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      );
    }

    const renderModal = () => {
      if (!modalState.show) {
        return null;
      }
      return (
        <Modal
          title={modalState.title}
          subtext={modalState.subtext}
          cancelButton={modalState.hasCancelButton}
          confirmButtonText={modalState.confirmButtonLabel}
          confirmButtonAction={modalState.onConfirmHandler}
          icon={modalState.icon}
          loading={modalState.loading}
          onCancel={modalState.onCancelHandler}
        />
      )
    }

    return (
      <>
        <Helmet>
          <title>Avanade Research - Admin</title>
        </Helmet>
        <div page="topics">
          <div className="w-[calc(100%-40px)] min-h-[calc(100%-40px)] m-[20px] bg-white rounded-2xl p-10 shadow-lg text-sm">
            <div className="max-w-[1280px] mx-auto">
              <div className="flex w-full">
                <div className="block w-full">
                  <div className="text-lg font-semibold">Topics</div>
                  <div className="text-gray-600">
                    Manage topic content for the platform.
                  </div>
                </div>
                <div className="content-center">
                  <div className="text-nowrap">
                    <Button
                      buttonClassName="py-2 px-5"
                      onClick={addTopicHandler}
                    >
                      Add Topic
                    </Button>
                  </div>
                </div>
              </div>

              <div className="mt-10">
                <div className="flex font-semibold mb-10">
                  <div className="w-[30%]">
                    <InputText
                      label="Search"
                      value={state.filter.search}
                      onBlur={(e) => setState({ ...state, filter: { ...state.filter, search: e.target.value } })}
                      onKeyDown={(e) =>
                        e.key === "Enter" && document.activeElement.blur()
                      }
                      onChange={(e) => setState({ ...state, filter: { ...state.filter, search: e.target.value } })}
                    />
                  </div>
                  <div className="flex items-center gap-12 ml-5">
                    <div className="w-[10%]">
                      <Button
                        className="mt-7"
                        buttonClassName="py-2 px-5"
                        onClick={filterHandler}>
                        Search
                      </Button>
                    </div>
                    <div className="w-[10%] ml-5">
                      <Button
                        className="mt-7"
                        buttonClassName="py-2 px-5"
                        onClick={resetFilterHandler}>
                        Clear
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              { renderList() }

              <Pagination
                currentPage={state.filter.page}
                totalItems={state.total_by_filter}
                itemsPerPage={state.filter.limit}
                setCurrentPage={pageChangeHandler} />
              
            </div>
          </div>

          {state.is_loading && <Loader />}
        </div>
        { renderModal() }
      </>
    );
};

export default Topics;
